import React, { useState, useEffect } from 'react';
import { getFullApiUrl } from '../config';
import { addAuthHeader } from '../utils/auth';

const FinancialAnalysisUploadSection = ({ onExtractionComplete, onProcessingChange }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [processingSteps, setProcessingSteps] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  
  // Get LLM configuration from localStorage (internal use only)
  const llmConfig = JSON.parse(localStorage.getItem('llmConfig') || '{}');
  const extractionProvider = llmConfig.extractionProvider || 'gemini';
  const extractionModel = llmConfig[`${extractionProvider}Model`] || (extractionProvider === 'groq' ? 'llama-3.3-70b-versatile' : 'gemini-2.5-pro');

  // Prevent page navigation when processing
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isProcessing) {
        // Standard way to show confirmation dialog
        e.preventDefault();
        // Modern browsers ignore custom message, but we can still set it
        e.returnValue = 'Proses analisis sedang berjalan. Jika Anda meninggalkan halaman ini, hasil analisis mungkin akan hilang. Apakah Anda yakin ingin meninggalkan halaman ini?';
        return e.returnValue;
      }
    };

    // Add event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isProcessing]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFiles(files);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFiles(files);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setUploadStatus(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const updateProcessingStep = (step, status = 'processing') => {
    setProcessingSteps(prev => {
      const updated = [...prev];
      const existingIndex = updated.findIndex(s => s.name === step);
      if (existingIndex >= 0) {
        updated[existingIndex].status = status;
      } else {
        updated.push({ name: step, status });
      }
      return updated;
    });
  };

  const handleFiles = (files) => {
    // Validate file types
    const invalidFiles = files.filter(f => !f.name.toLowerCase().endsWith('.pdf'));
    if (invalidFiles.length > 0) {
      setUploadStatus({
        type: 'error',
        message: 'Harap pilih file PDF saja (Laporan Keuangan).'
      });
      return;
    }

    // Validate file size (100MB total limit)
    const currentTotalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
    const newFilesTotalSize = files.reduce((sum, f) => sum + f.size, 0);
    const totalSize = currentTotalSize + newFilesTotalSize;
    
    if (totalSize > 100 * 1024 * 1024) {
      setUploadStatus({
        type: 'error',
        message: 'Total ukuran file harus kurang dari 100MB.'
      });
      return;
    }

    // Add new files to selected files list
    setSelectedFiles(prev => [...prev, ...files]);
    setUploadStatus({
      type: 'success',
      message: `${files.length} file berhasil ditambahkan. Klik tombol "Process" untuk memulai analisis.`
    });
  };

  const handleProcess = async () => {
    if (selectedFiles.length === 0) {
      setUploadStatus({
        type: 'error',
        message: 'Harap pilih file terlebih dahulu.'
      });
      return;
    }

    setIsProcessing(true);
    setProcessingSteps([]);
    
    // Notify parent about processing state
    if (onProcessingChange) {
      onProcessingChange(true);
    }

    // Get API key from localStorage (only for Groq)
    // For Gemini, API key is always retrieved from backend environment variable
    const providerApiKey = extractionProvider === 'groq' ? llmConfig.groqApiKey : null;
    
    setUploadStatus({
      type: 'info',
      message: `Memproses ${selectedFiles.length} file laporan keuangan...`
    });

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      // Show processing steps
      updateProcessingStep('Mengunggah dokumen', 'processing');
      updateProcessingStep('Optical Character Recognition', 'pending');
      updateProcessingStep('Ekstraksi data keuangan', 'pending');

      // Add provider, model, and API key as query parameters
      const queryParams = new URLSearchParams({
        provider: extractionProvider,
        model: extractionModel,
      });
      
      // Only send API key for Groq, not for Gemini
      // For Gemini, API key is always retrieved from backend environment variable
      if (extractionProvider === 'groq' && providerApiKey && providerApiKey.trim()) {
        queryParams.append('api_key', providerApiKey.trim());
      }

      // Prepare headers with authentication
      // Don't set Content-Type for FormData - browser will set it automatically with boundary
      const headers = addAuthHeader({});
      
      const response = await fetch(`${getFullApiUrl('FINANCIAL_ANALYSIS')}?${queryParams}`, {
        method: 'POST',
        headers: headers,
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.success) {
        updateProcessingStep('Mengunggah dokumen', 'completed');
        updateProcessingStep('Optical Character Recognition', 'completed');
        updateProcessingStep('Ekstraksi data keuangan', 'completed');

        setUploadStatus({
          type: 'success',
          message: `Berhasil mengekstrak ${result.records_count} metrik keuangan dari ${result.years_analyzed.length} periode analisis`
        });

        // Clear selected files after successful processing
        setSelectedFiles([]);

        // Pass extracted pivot data directly to parent
        if (onExtractionComplete) {
          onExtractionComplete(result.pivot_dataframe, result.years_analyzed);
        }
      } else {
        throw new Error(result.detail || result.message || 'Gagal memproses PDF');
      }
    } catch (error) {
      console.error('Financial analysis error:', error);
      updateProcessingStep('Mengunggah dokumen', 'error');
      setUploadStatus({
        type: 'error',
        message: `Gagal memproses dokumen: ${error.message || 'Terjadi kesalahan pada sistem'}`
      });
    } finally {
      setIsProcessing(false);
      
      // Notify parent about processing state
      if (onProcessingChange) {
        onProcessingChange(false);
      }
    }
  };

  const getStepIcon = (status) => {
    switch (status) {
      case 'completed': return 'fa-check-circle';
      case 'processing': return 'fa-spinner fa-spin';
      case 'error': return 'fa-exclamation-triangle';
      default: return 'fa-clock';
    }
  };
  
  const getStepIconColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-500';
      case 'processing': return 'text-blue-500';
      case 'error': return 'text-red-500';
      default: return 'text-gray-400';
    }
  };

  return (
    <section className="mb-8">
      <div 
        className={`bg-white dark:bg-gray-800 rounded-lg border border-dashed p-8 text-center transition-colors ${
          isDragOver 
            ? 'border-primary-400 dark:border-primary-500 bg-primary-50 dark:bg-primary-900/30' 
            : 'border-gray-300 dark:border-gray-600'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="max-w-md mx-auto">
          <div className="text-primary-600 mb-4">
            <i className="fa-solid fa-file-invoice-dollar text-4xl"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-2 transition-colors duration-300">
            Analisis Laporan Keuangan
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-300 mb-6 transition-colors duration-300">
            Unggah dokumen PDF Laporan Keuangan (Neraca & Laba Rugi) untuk analisis otomatis dan ekstraksi metrik finansial
          </p>
          <label htmlFor="financial-file-upload" className="cursor-pointer">
            <span className={`font-medium py-2.5 px-5 rounded-lg inline-flex items-center ${
              isProcessing 
                ? 'bg-gray-400 text-white cursor-not-allowed' 
                : 'bg-primary-600 hover:bg-primary-700 text-white'
            }`}>
              <i className="fa-solid fa-upload mr-2"></i>
              {isProcessing ? 'Menganalisis dengan AI...' : 'Upload File'}
            </span>
            <input
              id="financial-file-upload"
              type="file"
              className="hidden"
              accept=".pdf"
              multiple
              onChange={handleFileSelect}
              disabled={isProcessing}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 transition-colors duration-300">
              Format yang didukung: PDF. Maksimal total ukuran: 100MB
            </p>
          </label>
          
          {/* Status Message */}
          {uploadStatus && (
            <div className={`mt-4 p-3 rounded-lg text-sm transition-colors duration-300 ${
              uploadStatus.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
              uploadStatus.type === 'error' ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
              'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
            }`}>
              {uploadStatus.message}
            </div>
          )}
          
          <div className="mt-4 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">
            <i className="fa-solid fa-shield-check mr-2 text-primary-600 dark:text-primary-400"></i>
            <span>Diproses dengan teknologi AI terenkripsi</span>
          </div>
        </div>
      </div>

      {/* File List */}
      {selectedFiles.length > 0 && (
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 overflow-hidden transition-colors duration-300">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h4 className="text-lg font-medium text-gray-800 dark:text-white flex items-center transition-colors duration-300">
              <i className="fa-solid fa-file-pdf mr-2 text-primary-600 dark:text-primary-400"></i>
              File Terpilih ({selectedFiles.length})
            </h4>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {selectedFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                  <div className="flex items-center flex-1 min-w-0">
                    <i className="fa-solid fa-file-pdf text-red-500 dark:text-red-400 mr-3 text-lg transition-colors duration-300"></i>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-white truncate transition-colors duration-300">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 transition-colors duration-300">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveFile(index)}
                    disabled={isProcessing}
                    className={`ml-3 p-2 rounded-lg transition-colors ${
                      isProcessing 
                        ? 'bg-gray-200 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                        : 'bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400'
                    }`}
                    title="Hapus file"
                  >
                    <i className="fa-solid fa-trash text-sm"></i>
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={handleProcess}
                disabled={isProcessing || selectedFiles.length === 0}
                className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center transition-colors ${
                  isProcessing || selectedFiles.length === 0
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-primary-600 hover:bg-primary-700 text-white'
                }`}
              >
                {isProcessing ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    Memproses...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-play mr-2"></i>
                    Process ({selectedFiles.length} file)
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Processing Warning */}
      {isProcessing && (
        <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <i className="fa-solid fa-exclamation-triangle text-yellow-600 text-xl"></i>
            </div>
            <div className="ml-3 flex-1">
              <h4 className="text-sm font-medium text-yellow-800 mb-1">
                Proses Analisis Sedang Berjalan
              </h4>
              <p className="text-sm text-yellow-700">
                Harap <strong>tetap berada di halaman ini</strong> sampai proses analisis selesai. 
                Jika Anda meninggalkan halaman ini, hasil analisis mungkin akan hilang dan Anda perlu memproses ulang.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Processing Steps */}
      {processingSteps.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h4 className="text-lg font-medium text-gray-800 flex items-center">
              <i className="fa-solid fa-cogs mr-2 text-primary-600"></i>
              Status Pemrosesan
            </h4>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {processingSteps.map((step, index) => (
                <div key={index} className="flex items-center">
                  <i className={`fa-solid ${getStepIcon(step.status)} ${getStepIconColor(step.status)} mr-3 ${step.status === 'completed' ? 'text-lg' : ''}`}></i>
                  <span className={`text-sm ${
                    step.status === 'completed' ? 'text-green-700 font-medium' :
                    step.status === 'processing' ? 'text-blue-700' :
                    step.status === 'error' ? 'text-red-700' :
                    'text-gray-500'
                  }`}>
                    {step.name}
                  </span>
                  {step.status === 'processing' && (
                    <span className="ml-2 text-xs text-blue-600 animate-pulse">
                      Sedang diproses...
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default FinancialAnalysisUploadSection;

